import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function TilingPage() {
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [callbackSubmitted, setCallbackSubmitted] = useState(false);
  const [callbackData, setCallbackData] = useState({
    name: '',
    phone: '',
    email: '',
    preferredTime: 'morning',
    message: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessType: 'residential',
    teamSize: '1-2'
  });

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    
    const timer = setTimeout(() => {
      if (!formSubmitted) {
        setShowLeadForm(true);
      }
    }, 8000);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, [formSubmitted]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('businessType', formData.businessType);
      formBody.append('teamSize', formData.teamSize);
      
      await fetch('https://readdy.ai/api/form/d69b95vmvg9ih2c79ul0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      
      setFormSubmitted(true);
      setShowLeadForm(false);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCallbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', callbackData.name);
      formBody.append('phone', callbackData.phone);
      formBody.append('email', callbackData.email);
      formBody.append('preferredTime', callbackData.preferredTime);
      formBody.append('message', callbackData.message);
      
      await fetch('https://readdy.ai/api/form/d69b95vmvg9ih2c79ulg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      
      setCallbackSubmitted(true);
      setShowCallbackForm(false);
      setTimeout(() => setCallbackSubmitted(false), 5000);
    } catch (error) {
      console.error('Callback form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: 'ri-layout-grid-line',
      title: 'Floor Tiling',
      description: 'Generate detailed quotes for floor tiling including porcelain, ceramic, natural stone, and large format tiles with waste calculations.',
      price: 'From $65/m²'
    },
    {
      icon: 'ri-drop-line',
      title: 'Bathroom Tiling',
      description: 'Complete bathroom tiling quotes including waterproofing, wall tiles, floor tiles, niches, and feature walls with material breakdowns.',
      price: 'From $2,800'
    },
    {
      icon: 'ri-home-4-line',
      title: 'Kitchen Splashbacks',
      description: 'Quote kitchen splashback installations including subway tiles, mosaics, and feature tiles with precise measurements.',
      price: 'From $850'
    },
    {
      icon: 'ri-building-line',
      title: 'Commercial Tiling',
      description: 'Create quotes for commercial projects including retail, hospitality, and office spaces with compliance documentation.',
      price: 'From $55/m²'
    },
    {
      icon: 'ri-sun-line',
      title: 'Outdoor Tiling',
      description: 'Quote outdoor areas including patios, pool surrounds, and balconies with slip-resistant tile specifications.',
      price: 'From $85/m²'
    },
    {
      icon: 'ri-tools-line',
      title: 'Tile Repairs',
      description: 'Quick quotes for tile repairs, re-grouting, and replacement work with matching tile sourcing assistance.',
      price: 'From $180'
    }
  ];

  const stats = [
    { value: '14+', label: 'Hours Saved Weekly', icon: 'ri-time-line' },
    { value: '2,400+', label: 'Tilers Using It', icon: 'ri-team-line' },
    { value: '89%', label: 'Quote Win Rate', icon: 'ri-trophy-line' },
    { value: '$11K', label: 'Avg Monthly Revenue Increase', icon: 'ri-money-dollar-circle-line' }
  ];

  const testimonials = [
    {
      name: 'Marco Rossi',
      business: 'Rossi Tiling Solutions',
      specialty: 'Bathroom Specialist',
      image: 'https://public.readdy.ai/ai/img_res/c7e007db2c42a779f74507727577cd42.jpg',
      quote: 'The bathroom quoting feature is incredible. I can now include waterproofing, all tiles, and labour in one professional quote. My clients love the detailed breakdowns and 3D visualizations.',
      results: 'Increased monthly revenue by $13,000'
    },
    {
      name: 'Sophie Chen',
      business: 'Precision Tile Works',
      specialty: 'Large Format Tiles',
      image: 'https://readdy.ai/api/search-image?query=professional%20female%20tiler%20with%20large%20format%20premium%20porcelain%20tiles%20and%20advanced%20leveling%20system%20tools%20wearing%20quality%20work%20clothes%20confident%20pose%20in%20modern%20luxury%20residential%20setting%20natural%20daylight%20showing%20expertise%20cinematic%20quality&width=400&height=400&seq=tile-test-premium-2&orientation=squarish',
      quote: 'Quoting large format tile jobs used to be tricky with all the leveling system calculations. Now the system handles everything automatically including waste factors. Game changer!',
      results: 'Won 22 large format jobs in 2 months'
    },
    {
      name: 'James Murphy',
      business: 'Murphy Commercial Tiling',
      specialty: 'Commercial Projects',
      image: 'https://readdy.ai/api/search-image?query=experienced%20tiler%20on%20commercial%20construction%20site%20with%20professional%20tiling%20equipment%20and%20materials%20wearing%20safety%20gear%20confident%20appearance%20modern%20building%20interior%20natural%20lighting%20showing%20quality%20workmanship%20ultra%20sharp%20cinematic%20photography&width=400&height=400&seq=tile-test-premium-3&orientation=squarish',
      quote: 'Managing multiple commercial tiling projects was overwhelming. Now I can generate detailed quotes with compliance docs in minutes and track everything in one place.',
      results: 'Secured 5 major commercial contracts'
    }
  ];

  const supplierPricing = [
    {
      category: 'Ceramic & Porcelain',
      items: ['Floor tiles', 'Wall tiles', 'Large format', 'Mosaics'],
      icon: 'ri-layout-grid-line'
    },
    {
      category: 'Natural Stone',
      items: ['Marble', 'Travertine', 'Slate', 'Granite'],
      icon: 'ri-stack-line'
    },
    {
      category: 'Adhesives & Grouts',
      items: ['Tile adhesive', 'Grout', 'Sealers', 'Primers'],
      icon: 'ri-paint-bucket-line'
    },
    {
      category: 'Tools & Accessories',
      items: ['Leveling systems', 'Spacers', 'Trims', 'Waterproofing'],
      icon: 'ri-tools-line'
    }
  ];

  const caseStudies = [
    {
      title: 'Bathroom Renovation Business Growth',
      tiler: 'Elite Bathroom Tiling Sydney',
      challenge: 'Struggled to provide quick, accurate quotes for bathroom renovations. Lost jobs to competitors with faster turnaround times.',
      solution: 'Implemented automated bathroom quoting with waterproofing calculations and real-time tile pricing from suppliers.',
      results: [
        'Quote generation time reduced from 2 hours to 18 minutes',
        'Bathroom job bookings increased by 290%',
        'Average job value increased from $4,500 to $6,200',
        'Customer satisfaction score improved to 4.9/5'
      ],
      image: 'https://readdy.ai/api/search-image?query=stunning%20luxury%20modern%20bathroom%20renovation%20with%20beautiful%20premium%20porcelain%20tiles%20being%20installed%20by%20professional%20tiler%20showing%20quality%20craftsmanship%20herringbone%20pattern%20feature%20wall%20bright%20clean%20contemporary%20design%20gleaming%20finishes%20cinematic%20lighting&width=800&height=500&seq=tile-case-premium-1&orientation=landscape'
    },
    {
      title: 'Commercial Tiling Contractor Efficiency',
      tiler: 'Metro Tile Contractors Melbourne',
      challenge: 'Managing quotes for large commercial projects was time-consuming. Material calculations and waste factors led to pricing errors.',
      solution: 'Adopted comprehensive quoting system with automatic area calculations and supplier integration.',
      results: [
        'Won 8 major commercial contracts worth $320K total',
        'Material cost accuracy improved by 96%',
        'Project quoting time reduced by 75%',
        'Profit margins increased by 20%'
      ],
      image: 'https://readdy.ai/api/search-image?query=professional%20commercial%20tiling%20installation%20in%20modern%20luxury%20retail%20space%20with%20large%20format%20premium%20porcelain%20tiles%20being%20laid%20by%20experienced%20tilers%20showing%20precision%20work%20and%20quality%20finish%20bright%20interior%20gleaming%20materials%20cinematic%20photography&width=800&height=500&seq=tile-case-premium-2&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Floating Callback Button */}
      <button
        onClick={() => setShowCallbackForm(true)}
        className="fixed left-6 bottom-6 z-40 flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-full shadow-2xl hover:shadow-teal-500/30 hover:scale-105 transition-all duration-300 cursor-pointer group"
      >
        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
          <i className="ri-phone-line text-xl"></i>
        </div>
        <div className="pr-2">
          <div className="font-semibold text-sm whitespace-nowrap">Request Callback</div>
          <div className="text-teal-100 text-xs whitespace-nowrap">We'll call you back</div>
        </div>
      </button>

      {/* Callback Request Modal */}
      {showCallbackForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            <button
              onClick={() => setShowCallbackForm(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-xl text-gray-600"></i>
            </button>
            
            <div className="bg-gradient-to-br from-teal-600 to-cyan-600 p-8 text-white">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
                <i className="ri-phone-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-2">Request a Callback</h3>
              <p className="text-teal-100">Leave your details and we'll call you back within 2 hours during business hours.</p>
            </div>
            
            <form 
              id="tiling-callback-form"
              data-readdy-form
              onSubmit={handleCallbackSubmit}
              className="p-8 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={callbackData.name}
                  onChange={(e) => setCallbackData({ ...callbackData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                  placeholder="John Smith"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={callbackData.phone}
                  onChange={(e) => setCallbackData({ ...callbackData, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                  placeholder="0412 345 678"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={callbackData.email}
                  onChange={(e) => setCallbackData({ ...callbackData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                  placeholder="john@email.com (optional)"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Call Time</label>
                <select
                  name="preferredTime"
                  value={callbackData.preferredTime}
                  onChange={(e) => setCallbackData({ ...callbackData, preferredTime: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="morning">Morning (9am - 12pm)</option>
                  <option value="afternoon">Afternoon (12pm - 5pm)</option>
                  <option value="evening">Evening (5pm - 7pm)</option>
                  <option value="anytime">Anytime</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">What can we help with?</label>
                <textarea
                  name="message"
                  value={callbackData.message}
                  onChange={(e) => setCallbackData({ ...callbackData, message: e.target.value.slice(0, 500) })}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm resize-none"
                  placeholder="Tell us briefly what you'd like to discuss..."
                />
                <div className="text-xs text-gray-400 text-right mt-1">{callbackData.message.length}/500</div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-phone-line"></i>
                    Request Callback
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Callback Success Toast */}
      {callbackSubmitted && (
        <div className="fixed bottom-6 left-6 z-50 bg-teal-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-bottom duration-300">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-check-line text-2xl"></i>
          </div>
          <div>
            <div className="font-bold">Callback Requested!</div>
            <div className="text-teal-100 text-sm">We'll call you back soon</div>
          </div>
        </div>
      )}

      {/* Floating Lead Capture Form */}
      {showLeadForm && !formSubmitted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            <button
              onClick={() => setShowLeadForm(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl text-gray-600"></i>
            </button>
            
            <div className="bg-gradient-to-br from-teal-600 to-cyan-600 p-8 text-white">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full text-sm font-medium mb-4">
                <i className="ri-gift-line"></i>
                Limited Time Offer
              </div>
              <h3 className="text-2xl font-bold mb-2">Get Your Free Tiling Business Audit</h3>
              <p className="text-teal-100">Plus 30-day extended trial when you book a demo today</p>
            </div>
            
            <form 
              id="tiling-lead-form"
              data-readdy-form
              onSubmit={handleFormSubmit}
              className="p-8 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                  placeholder="John Smith"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                  placeholder="john@tiling.com.au"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                  placeholder="0412 345 678"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Specialization</label>
                  <select
                    name="businessType"
                    value={formData.businessType}
                    onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="bathroom">Bathroom</option>
                    <option value="outdoor">Outdoor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Team Size</label>
                  <select
                    name="teamSize"
                    value={formData.teamSize}
                    onChange={(e) => setFormData({ ...formData, teamSize: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="1-2">Just me / 1-2</option>
                    <option value="3-5">3-5 people</option>
                    <option value="6-10">6-10 people</option>
                    <option value="10+">10+ people</option>
                  </select>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-calendar-check-line"></i>
                    Get My Free Audit + Demo
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Sticky Bottom CTA Bar */}
      {showStickyBar && !formSubmitted && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-teal-600 to-cyan-600 shadow-2xl shadow-black/20 transform transition-transform duration-300">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full">
                <i className="ri-layout-grid-fill text-white"></i>
                <span className="text-white text-sm font-medium">Tilers Only</span>
              </div>
              <div className="text-white">
                <div className="font-bold text-lg">Get 30% Off Your First 3 Months</div>
                <div className="text-teal-100 text-sm hidden sm:block">Plus free business audit worth $499</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowLeadForm(true)}
                className="px-6 py-3 bg-white text-teal-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg cursor-pointer whitespace-nowrap"
              >
                <i className="ri-gift-line mr-2"></i>
                Claim Offer
              </button>
              <button
                onClick={() => setShowStickyBar(false)}
                className="p-2 text-white/80 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {formSubmitted && (
        <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-bottom duration-300">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-check-line text-2xl"></i>
          </div>
          <div>
            <div className="font-bold">Thanks! We'll be in touch soon</div>
            <div className="text-emerald-100 text-sm">Check your email for confirmation</div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-cyan-50 to-emerald-50"></div>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 rounded-full mb-6">
                <i className="ri-layout-grid-fill text-teal-600"></i>
                <span className="text-sm font-medium text-teal-900">For Professional Tilers</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Tiling Quoting<br />
                <span className="text-teal-600">Made Simple</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Generate professional tiling quotes in minutes. From bathroom renovations to commercial projects, 
                streamline your quoting process and win more jobs with accurate, detailed estimates.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <Link 
                  to="/demo" 
                  className="px-8 py-4 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  Book Free Demo
                </Link>
                <Link 
                  to="/invoice-demo" 
                  className="px-8 py-4 bg-white text-teal-600 rounded-lg font-semibold border-2 border-teal-600 hover:bg-teal-50 transition-all duration-300 whitespace-nowrap"
                >
                  Try Quote Generator
                </Link>
              </div>
              
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-teal-600"></i>
                  <span><strong className="text-gray-900">2.8 min</strong> avg quote time</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-teal-600"></i>
                  <span>Waterproofing Included</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-3xl transform rotate-3"></div>
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20australian%20tiler%20installing%20beautiful%20premium%20porcelain%20floor%20tiles%20in%20luxury%20modern%20residential%20setting%20using%20advanced%20leveling%20system%20and%20professional%20tiling%20tools%20wearing%20quality%20work%20clothes%20showing%20expertise%20and%20precision%20craftsmanship%20gleaming%20tiles%20natural%20lighting%20cinematic%20quality&width=600&height=700&seq=tile-hero-premium-1&orientation=portrait"
                alt="Professional Tiler"
                className="relative rounded-3xl shadow-2xl w-full h-auto object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-green-600"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">$6,850</div>
                    <div className="text-sm text-gray-600">Bathroom quote sent in 8 min</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-teal-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${stat.icon} text-3xl text-white`}></i>
                </div>
                <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-teal-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Tiling Trade Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to quote tiling jobs quickly and accurately
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group bg-white border-2 border-gray-100 rounded-2xl p-8 hover:border-teal-600 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-teal-600 transition-colors duration-300">
                  <i className={`${feature.icon} text-3xl text-teal-600 group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{feature.description}</p>
                <div className="text-teal-600 font-semibold">{feature.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supplier Pricing Integration */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Real-Time Tile & Material Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Integrated pricing from major Australian tile suppliers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {supplierPricing.map((category, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 border-2 border-teal-100 shadow-lg"
              >
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${category.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">{category.category}</h3>
                <ul className="space-y-2">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-teal-600 mt-0.5"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-6">Integrated with leading suppliers:</p>
            <div className="flex flex-wrap justify-center gap-8 items-center">
              <div className="text-2xl font-bold text-gray-400">Beaumont Tiles</div>
              <div className="text-2xl font-bold text-gray-400">National Tiles</div>
              <div className="text-2xl font-bold text-gray-400">Tile Warehouse</div>
              <div className="text-2xl font-bold text-gray-400">TileCloud</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Tilers Across Australia
            </h2>
            <p className="text-xl text-gray-600">
              See how tilers are growing their businesses
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.business}</div>
                    <div className="text-xs text-teal-600 font-medium">{testimonial.specialty}</div>
                  </div>
                </div>
                
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400"></i>
                  ))}
                </div>
                
                <p className="text-gray-700 mb-4 leading-relaxed">"{testimonial.quote}"</p>
                
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm font-semibold text-green-600">
                    <i className="ri-arrow-up-line"></i>
                    {testimonial.results}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Tiling Business Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              Real results from tilers who transformed their businesses
            </p>
          </div>

          <div className="space-y-16">
            {caseStudies.map((study, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-50 to-teal-50 rounded-3xl overflow-hidden shadow-xl"
              >
                <div className="grid lg:grid-cols-2 gap-8">
                  <div className="p-12">
                    <div className="inline-block px-4 py-2 bg-teal-600 text-white rounded-full text-sm font-semibold mb-6">
                      Case Study
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">{study.title}</h3>
                    <div className="text-teal-600 font-semibold mb-6">{study.tiler}</div>
                    
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-error-warning-line text-red-500"></i>
                          Challenge
                        </h4>
                        <p className="text-gray-700">{study.challenge}</p>
                      </div>
                      
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-lightbulb-line text-teal-600"></i>
                          Solution
                        </h4>
                        <p className="text-gray-700">{study.solution}</p>
                      </div>
                      
                      <div>
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <i className="ri-trophy-line text-green-600"></i>
                          Results
                        </h4>
                        <ul className="space-y-2">
                          {study.results.map((result, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-gray-700">
                              <i className="ri-check-line text-green-600 mt-1"></i>
                              <span>{result}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="relative h-full min-h-[400px]">
                    <img 
                      src={study.image}
                      alt={study.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-cyan-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Tiling Business?
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            Join 2,400+ tilers who are quoting faster, winning more jobs, and growing their revenue
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Link 
              to="/demo" 
              className="px-8 py-4 bg-white text-teal-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Book Your Free Demo
            </Link>
            <Link 
              to="/invoice-demo" 
              className="px-8 py-4 bg-teal-700 text-white rounded-lg font-semibold hover:bg-teal-800 transition-all duration-300 border-2 border-white whitespace-nowrap"
            >
              Try Quote Generator
            </Link>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6 text-teal-100">
            <div className="flex items-center gap-2">
              <i className="ri-check-line"></i>
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line"></i>
              <span>14-day free trial</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line"></i>
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
